import {
  type ComponentDefinition,
  type ComponentNode,
  type EnergyBudget,
  type Issue,
  type System,
  type Terminal,
} from "../types";
import { getDefinition, type DefLookup } from "../catalog";
import { isAcOnlyComponent } from "../ac";
import { effectiveTerminals } from "../effective";
import { dcPortTerminalGroups, isDirectDcSource } from "../component-ports";
import { applyStateToAmp, getEffectiveStateOption, getStateOption } from "../simulate/states";
import { computeEnergyFlow, type EnergyFlow } from "./energy-flow";
import { connectedRuntime } from "./connected-runtime";
import { computeBatteryStacks } from "../battery-stack";
import { DEFAULT_SOC, reserveSocForChemistry } from "../battery-reserve";
import { loadCurrentsAtVoltage } from "../component-current";
import { DEFAULT_NOMINAL_V, resolveVoltageDomains } from "../voltage-domain";
import { resolveTerminalPotentials, type TerminalPotentials } from "../terminal-voltage";
import { buildGraph, computeActiveSourceCircuits, isDcSideSupplied } from "./graph";

/**
 * Domain voltage at one component, over the terminals the filter accepts: a
 * charger harvests at its OUTPUT voltage, a load draws at its INPUT voltage.
 */
function nodeOperatingVoltage(
  nodeId: string,
  system: System,
  defLookup: DefLookup,
  domains: ReturnType<typeof resolveVoltageDomains>,
  potentials: TerminalPotentials,
  acceptsTerminal?: (terminalId: string) => boolean,
): number {
  const node = system.nodes.find((n) => n.id === nodeId);
  if (!node || node.kind !== "component") return DEFAULT_NOMINAL_V;
  const def = defLookup(node.definitionId, node.customDefinition);
  if (!def) return DEFAULT_NOMINAL_V;
  const terminals = def.terminals.filter(t => !acceptsTerminal || acceptsTerminal(t.id));
  for (const positive of terminals.filter(t => t.polarity === "positive")) {
    for (const negative of terminals.filter(t => t.polarity === "negative" || t.polarity === "ground")) {
      const measured = potentials.voltageBetween(`${nodeId}:${positive.id}`, `${nodeId}:${negative.id}`);
      if (measured != null && measured > 0) return measured;
    }
  }
  let best = 0;
  for (const c of system.cables) {
    let myTerm: string | undefined;
    if (c.fromNodeId === nodeId) myTerm = c.fromTerminalId;
    else if (c.toNodeId === nodeId) myTerm = c.toTerminalId;
    else continue;
    if (acceptsTerminal && !acceptsTerminal(myTerm)) continue;
    const dom = domains.domainByCable.get(c.id);
    if (!dom?.hasSource) continue;
    if (dom.nominalV > best) best = dom.nominalV;
  }
  return best > 0 ? best : DEFAULT_NOMINAL_V;
}

/** Draw belongs to the DC input pair, even when an inverter/charger also
 * exposes optional AC ports or a distribution device passes the rail onward. */
function consumptionPorts(def: ComponentDefinition): Terminal[][] {
  const inputs = dcPortTerminalGroups(def, "input");
  return inputs.length > 0 ? inputs : dcPortTerminalGroups(def, "bidi");
}

const SOLAR_DERATE = 0.75; // panel-to-battery efficiency

const labelFor = (n: ComponentNode, defShort?: string, defName?: string): string =>
  n.label ?? defShort ?? defName ?? "Component";

export type ComponentStates = Record<string, string>;
export type BatterySoc = Record<string, number>;
/** Per-load on/off toggle state. Missing entries default to active. */
export type LoadStates = Record<string, { active: boolean }>;

/** An undefined `energizedTerminals` is edit mode: treat everything as
 *  reachable. A node with no wired non-signal terminal is never live. */
function isNodeEnergized(
  node: ComponentNode,
  def: ComponentDefinition,
  system: System,
  energizedTerminals: ReadonlySet<string> | undefined,
  ports?: Terminal[][],
): boolean {
  if (!energizedTerminals) return true;
  if (ports && ports.length > 0) return ports.some(port =>
    port.some(t => t.polarity === "positive") &&
    port.some(t => t.polarity === "negative" || t.polarity === "ground") &&
    port.every(t => energizedTerminals.has(`${node.id}:${t.id}`)));
  const wiredNonSignal = def.terminals.filter((t) => {
    if (t.polarity === "signal") return false;
    return system.cables.some(
      (c) =>
        (c.fromNodeId === node.id && c.fromTerminalId === t.id) ||
        (c.toNodeId === node.id && c.toTerminalId === t.id),
    );
  });
  if (wiredNonSignal.length === 0) return false;
  return wiredNonSignal.every((t) =>
    energizedTerminals.has(`${node.id}:${t.id}`),
  );
}

/**
 * Instantaneous energy budget — everything is "right now", no per-day
 * integration. A load counts only when its toggle is on *and* reachable.
 */
export function computeEnergyBudget(
  system: System,
  defLookup: DefLookup,
  componentStates: ComponentStates = {},
  batterySoc: BatterySoc = {},
  loadStates: LoadStates = {},
  /** `nodeId:terminalId` vertices reachable from a battery. Omitted is edit mode. */
  energizedTerminals?: ReadonlySet<string>,
): EnergyBudget {
  return computeEnergySnapshot(system, defLookup, componentStates, batterySoc, loadStates, energizedTerminals).budget;
}

/** The summary and battery cards consume one power model. Keeping the signed
 * bus ledger beside its budget prevents overlays from falling back to local
 * cable sizing estimates that lose loads behind distribution components. */
export function computeEnergySnapshot(
  system: System,
  defLookup: DefLookup,
  componentStates: ComponentStates = {},
  batterySoc: BatterySoc = {},
  loadStates: LoadStates = {},
  energizedTerminals?: ReadonlySet<string>,
): { budget: EnergyBudget; flow: EnergyFlow } {
  const domains = resolveVoltageDomains(system, defLookup);
  const potentials = resolveTerminalPotentials(system, defLookup,
    buildGraph(system, defLookup, { excludeSense: true, simulate: { componentStates, loadStates } }), { componentStates, loadStates });

  // Ah and volts are a bank's, not a cell's: a 2P8S block of 202 Ah cells is
  // 404 Ah at 25.6 V, not 3232 Ah at 3.2 V. Watt-hours sum per battery either
  // way, so the energy figures below stay per battery.
  let batteryAh = 0;
  let largestBatteryVoltage = 0;
  for (const stack of computeBatteryStacks(system, defLookup).stacks) {
    batteryAh += stack.capacityAh;
    const v = stack.nominalV > 0 ? stack.nominalV : DEFAULT_NOMINAL_V;
    if (v > largestBatteryVoltage) largestBatteryVoltage = v;
  }
  let currentChargeWh = 0;
  let batteryCapacityWh = 0;
  let reserveFloorWh = 0;

  // Each battery is sized against its own voltage, not the system's.
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "battery") continue;
    const capAh = def.capacityAh ?? 0;
    const v = def.voltage ?? DEFAULT_NOMINAL_V;
    const capWh = capAh * v;
    batteryCapacityWh += capWh;
    const soc = batterySoc[n.id] ?? DEFAULT_SOC;
    currentChargeWh += capWh * soc;
    reserveFloorWh += capWh * reserveSocForChemistry(def.chemistry);
  }

  // A UI summary only; per-component voltages come from the domain map.
  const systemVoltage = largestBatteryVoltage > 0 ? largestBatteryVoltage : DEFAULT_NOMINAL_V;
  const usableBatteryWh = Math.max(0, batteryCapacityWh - reserveFloorWh);
  const reserveSocPct =
    batteryCapacityWh > 0 ? reserveFloorWh / batteryCapacityWh : 0;

  const sourceCircuits = energizedTerminals ? computeActiveSourceCircuits(system, defLookup, { componentStates, loadStates }) : undefined;
  let instantDrawW = 0;
  const consumptionBreakdown: EnergyBudget["consumptionBreakdown"] = [];

  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || (def.category !== "load" && def.ampDraw == null)) continue;
    const ports = consumptionPorts(def);
    const drawTerminals = new Set(ports.flat().map(t => t.id));

    const loadV = nodeOperatingVoltage(
      n.id,
      system,
      defLookup,
      domains,
      potentials,
      drawTerminals.size > 0 ? id => drawTerminals.has(id) : undefined,
    );
    const state = getEffectiveStateOption(def, n.id, { componentStates, loadStates });
    const watts = (applyStateToAmp(loadCurrentsAtVoltage(def, loadV).draw, state, "draw") ?? 0) * loadV;
    const isActive = def.category === "load" ? loadStates[n.id]?.active ?? true : true;
    const side = dcPortTerminalGroups(def, "input").length > 0 ? "input" : "bidi";
    const isReachable = isNodeEnergized(n, def, system, energizedTerminals, ports) &&
      (!sourceCircuits || ports.length === 0 || isDcSideSupplied(n, def, side, sourceCircuits));
    const live = isActive && isReachable;
    if (live) instantDrawW += watts;
    consumptionBreakdown.push({
      nodeId: n.id,
      label: labelFor(n, def.shortName, def.name),
      watts,
      isActive,
      isReachable,
    });
  }

  const solarWatts = new Map<string, number>();
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (def?.category !== "solar") continue;
    const opt = getStateOption(def, componentStates[n.id]);
    const factor = opt?.ampOverride === 0 ? 0 : (opt?.ampMultiplier ?? 1);
    if (isNodeEnergized(n, def, system, energizedTerminals)) {
      solarWatts.set(n.id, (def.watts ?? 0) * factor * SOLAR_DERATE);
    }
  }
  const flow = computeEnergyFlow(system, defLookup, { componentStates, loadStates, energizedTerminals },
    new Map(consumptionBreakdown.filter(load => load.isActive && load.isReachable).map(load => [load.nodeId, load.watts])), solarWatts);
  for (const [nodeId, watts] of flow.monitorDrawW) {
    const node = system.nodes.find(n => n.id === nodeId);
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    instantDrawW += watts;
    consumptionBreakdown.push({ nodeId, label: labelFor(node, def?.shortName, def?.name), watts, isActive: true, isReachable: true });
  }
  consumptionBreakdown.sort((a, b) => b.watts - a.watts);
  const { solar: solarW, alternator: alternatorW, shore: shoreW, other: otherW } = flow.harvest;
  instantDrawW += flow.conversionLossW;

  const instantHarvestW = solarW + alternatorW + shoreW + otherW;
  const netInstantW = instantHarvestW - instantDrawW;

  const { timeToEmptyHours, timeToFullHours } = connectedRuntime(system, defLookup, flow, batterySoc, DEFAULT_SOC);

  return { flow, budget: {
    currentChargeWh,
    reserveFloorWh,
    systemVoltage,
    batteryNominalAh: batteryAh,
    batteryCapacityWh,
    usableBatteryWh,
    reserveSocPct,
    instantDrawW,
    instantHarvestW,
    netInstantW,
    timeToEmptyHours,
    timeToFullHours,
    harvestBreakdown: { solar: solarW, alternator: alternatorW, shore: shoreW, other: otherW },
    consumptionBreakdown,
  } };
}

/** 24 h SoC trajectory, assuming the current net rate holds. */
export function computeSocTrajectory(input: {
  currentChargeWh: number;
  batteryCapacityWh: number;
  reserveFloorWh: number;
  netInstantW: number;
  hours?: number;
}): {
  series: number[];
  reserveFloorPct: number;
  firstReserveBreachHour: number | null;
} {
  const {
    currentChargeWh,
    batteryCapacityWh,
    reserveFloorWh,
    netInstantW,
    hours = 24,
  } = input;

  let charge = currentChargeWh;
  const series: number[] = [charge];
  let firstBreach: number | null = null;
  for (let h = 0; h < hours; h++) {
    charge = Math.max(
      0,
      Math.min(batteryCapacityWh, charge + netInstantW),
    );
    if (firstBreach === null && charge < reserveFloorWh) firstBreach = h + 1;
    series.push(charge);
  }

  return {
    series,
    reserveFloorPct:
      batteryCapacityWh > 0 ? reserveFloorWh / batteryCapacityWh : 0,
    firstReserveBreachHour: firstBreach,
  };
}

export function runEnergyDiagnostics(
  system: System,
  budget: EnergyBudget,
  defLookup: DefLookup = getDefinition,
): Issue[] {
  const issues: Issue[] = [];

  if (budget.batteryNominalAh === 0) {
    const batteries = system.nodes.filter(n => n.kind === "component" &&
      defLookup(n.definitionId, n.customDefinition)?.category === "battery");
    if (batteries.length > 0) {
      issues.push({
        code: "battery-capacity-unknown",
        severity: "info",
        title: { key: "diag.battery-capacity-unknown.title" },
        message: { key: "diag.battery-capacity-unknown.message" },
        fix: { key: "diag.battery-capacity-unknown.fix" },
        nodeIds: batteries.map(n => n.id),
      });
    } else if (system.nodes.some((n) => {
      if (n.kind !== "component") return false;
      const definition = defLookup(n.definitionId, n.customDefinition);
      // The DC energy model cannot require a battery for an AC-only drawing.
      // Unknown and mixed AC/DC equipment still retain the existing notice.
      return !definition || !isAcOnlyComponent({ terminals: effectiveTerminals(definition, n) });
    })) {
      issues.push({
        code: "no-battery",
        severity: "info",
        title: { key: "diag.no-battery.title" },
        message: { key: "diag.no-battery.message" },
      });
    }
    return issues;
  }

  if (budget.instantDrawW > 0 && !hasChargingComponent(system, defLookup)) {
    issues.push({
      code: "no-charging",
      severity: "warning",
      title: { key: "diag.no-charging.title" },
      message: {
        key: "diag.no-charging.message",
        vars: {
          watts: Math.round(budget.instantDrawW),
          hours: Number.isFinite(budget.timeToEmptyHours)
            ? budget.timeToEmptyHours.toFixed(1)
            : "∞",
        },
      },
      fix: { key: "diag.no-charging.fix" },
    });
  }

  return issues;
}

function hasChargingComponent(
  system: System,
  defLookup: DefLookup | undefined,
): boolean {
  if (!defLookup) return false;
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    if (def.category === "solar") return true;
    if (def.category === "charger") return true;
    if (isDirectDcSource(def)) return true;
  }
  return false;
}
